// Layer: infrastructure. Implements the TokenSource port.
//
// The console is a public client: it holds no secret and cannot mint a token. It is
// handed one by the identity provider and passes it through. Storing it in memory
// rather than localStorage is deliberate — a token in localStorage is readable by any
// script that gets injected into the page, and survives the tab that earned it.
import type { TokenSource } from "../application/ports";

export class InMemoryTokenSource implements TokenSource {
  private token: string | null;

  constructor(initial: string | null = null) {
    this.token = initial;
  }

  set(token: string | null): void {
    this.token = token;
  }

  async get(): Promise<string | null> {
    return this.token;
  }

  clear(): void {
    this.token = null;
  }
}

/**
 * Reads the credential the host page injected, then removes it from the DOM.
 *
 * The identity provider redirects back with the token in a `#fragment`, which never
 * reaches a server log the way a query string does. We consume it and clear the hash
 * so it does not survive in browser history or a shared URL.
 */
export function tokenFromLocation(location: Location, history: History): string | null {
  const hash = location.hash.startsWith("#") ? location.hash.slice(1) : location.hash;
  if (!hash) return null;
  const token = new URLSearchParams(hash).get("access_token");
  if (!token) return null;
  history.replaceState(null, "", `${location.pathname}${location.search}`);
  return token;
}
